import { createFileRoute, redirect } from "@tanstack/react-router";
import type { RouterContextWithSession } from "../../../router";
import { useState } from "react";
import { useAuth } from "../../../components/AuthProvider";
import { createRouteError } from "../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../components/RouteSkeleton";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Badge } from "../../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { toast } from "../../../components/ui/sonner";
import {
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  Users,
  RefreshCw,
  Plus,

  Settings,
  Trash2,
  Eye,
} from "lucide-react";

export const Route = createFileRoute("/_admin/calendar/")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  loader: async ({ context }) => {
    if (typeof window === "undefined") return;
    const { session } = context as RouterContextWithSession;
    if (!session) return;
    // Session validated — component can render immediately with sample data.
    // TODO: Replace with real calendar data fetch (ensureQueryData) in future phase.
  },
  pendingComponent: () => <PageSkeleton rows={4} variant="cards" />,
  errorComponent: createRouteError("Calendar Management"),
  component: CalendarPage,
});

// ── Types ─────────────────────────────────────────────────────────────────

type BookingStatus = "confirmed" | "pending" | "cancelled" | "completed";

interface Booking {
  id: string;
  title: string;
  clientName: string;
  date: string;
  time: string;
  duration: number; // minutes
  status: BookingStatus;
  notes?: string;
}

const STATUS_COLORS: Record<BookingStatus, string> = {
  confirmed: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  cancelled: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  completed: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
};

// ── Main Page ─────────────────────────────────────────────────────────────

function CalendarPage() {
  const { loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Redirecting to sign-in…</p>
      </div>
    );
  }

  return <AuthenticatedCalendar />;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function todayISO(): string {
  return new Date().toISOString().split("T")[0];
}

function tomorrowISO(): string {
  return new Date(Date.now() + 86400000).toISOString().split("T")[0];
}

// ── Authenticated sub-component ───────────────────────────────────────────

function AuthenticatedCalendar() {
  const [bookings, setBookings] = useState<Booking[]>([
    {
      id: "1",
      title: "Strategy Meeting",
      clientName: "Acme Corp",
      date: todayISO(),
      time: "09:00",
      duration: 60,
      status: "confirmed",
      notes: "Quarterly review",
    },
    {
      id: "2",
      title: "Design Review",
      clientName: "TechStart Inc",
      date: todayISO(),
      time: "14:00",
      duration: 45,
      status: "pending",
    },
    {
      id: "3",
      title: "Onboarding Call",
      clientName: "Global Solutions",
      date: tomorrowISO(),
      time: "10:30",
      duration: 30,
      status: "confirmed",
    },
  ]);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newBooking, setNewBooking] = useState({
    title: "",
    clientName: "",
    date: "",
    time: "",
    duration: "60",
  });

  // Stats
  const today = todayISO();
  const todayBookings = bookings.filter((b) => b.date === today).length;
  const pendingApproval = bookings.filter((b) => b.status === "pending").length;
  const completed = bookings.filter((b) => b.status === "completed").length;
  const completionRate =
    bookings.length > 0
      ? Math.round((completed / bookings.length) * 100)
      : 0;

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await new Promise((r) => setTimeout(r, 1000));
    setIsRefreshing(false);
    toast.success("Calendar data refreshed");
  };

  const handleCreateBooking = () => {
    if (!newBooking.title.trim() || !newBooking.date || !newBooking.time) {
      toast.error("Please fill in all required fields");
      return;
    }
    const booking: Booking = {
      id: crypto.randomUUID(),
      title: newBooking.title,
      clientName: newBooking.clientName,
      date: newBooking.date,
      time: newBooking.time,
      duration: parseInt(newBooking.duration) || 60,
      status: "pending",
    };
    setBookings((prev) => [...prev, booking]);
    setCreateDialogOpen(false);
    setNewBooking({ title: "", clientName: "", date: "", time: "", duration: "60" });
    toast.success("Booking created successfully");
  };

  const handleStatusChange = (id: string, status: BookingStatus) => {
    setBookings((prev) =>
      prev.map((b) => (b.id === id ? { ...b, status } : b))
    );
    toast.success(`Booking marked as ${status}`);
  };

  const handleDelete = (id: string) => {
    setBookings((prev) => prev.filter((b) => b.id !== id));
    toast.success("Booking deleted");
  };

  return (
    <main className="container mx-auto p-6 space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Calendar Management</h1>
          <p className="text-muted-foreground mt-1">
            Manage bookings, appointments, and calendar settings.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
          <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Booking
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          label="Total Bookings"
          value={String(bookings.length)}
          icon={<Calendar className="h-5 w-5 text-blue-500" />}
        />
        <StatCard
          label="Today's Meetings"
          value={String(todayBookings)}
          icon={<Clock className="h-5 w-5 text-orange-500" />}
        />
        <StatCard
          label="Pending Approval"
          value={String(pendingApproval)}
          icon={<AlertCircle className="h-5 w-5 text-yellow-500" />}
        />
        <StatCard
          label="Completion Rate"
          value={`${completionRate}%`}
          icon={<CheckCircle className="h-5 w-5 text-green-500" />}
        />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="bookings" className="space-y-4">
        <TabsList>
          <TabsTrigger value="bookings">
            <Calendar className="mr-2 h-4 w-4" />
            Bookings
          </TabsTrigger>

          <TabsTrigger value="settings">
            <Settings className="mr-2 h-4 w-4" />
            Settings
          </TabsTrigger>
        </TabsList>

        {/* Bookings Tab */}
        <TabsContent value="bookings" className="space-y-4">
          {bookings.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Bookings</h3>
                <p className="text-sm text-muted-foreground mb-4 text-center">
                  No bookings scheduled. Create your first booking to get
                  started.
                </p>
                <Button onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Booking
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {bookings.map((booking) => (
                <Card key={booking.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-sm">
                            {booking.title}
                          </h4>
                          <Badge
                            className={STATUS_COLORS[booking.status]}
                          >
                            {booking.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {booking.clientName || "No client"}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {booking.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {booking.time} ({booking.duration} min)
                          </span>
                        </div>
                        {booking.notes && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {booking.notes}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Select
                          value={booking.status}
                          onValueChange={(value) =>
                            handleStatusChange(
                              booking.id,
                              value as BookingStatus
                            )
                          }
                        >
                          <SelectTrigger className="w-[130px] h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="confirmed">
                              Confirmed
                            </SelectItem>
                            <SelectItem value="completed">
                              Completed
                            </SelectItem>
                            <SelectItem value="cancelled">
                              Cancelled
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(booking.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>



        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Calendar Settings</CardTitle>
              <CardDescription>
                Configure your calendar preferences and availability
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="business-hours-start">Business Hours Start</Label>
                <Input
                  id="business-hours-start"
                  type="time"
                  defaultValue="09:00"
                  className="w-[200px]"
                />
              </div>
              <div>
                <Label htmlFor="business-hours-end">Business Hours End</Label>
                <Input
                  id="business-hours-end"
                  type="time"
                  defaultValue="17:00"
                  className="w-[200px]"
                />
              </div>
              <div>
                <Label htmlFor="default-duration">
                  Default Meeting Duration (minutes)
                </Label>
                <Input
                  id="default-duration"
                  type="number"
                  defaultValue="60"
                  min="15"
                  step="15"
                  className="w-[200px]"
                />
              </div>
              <div>
                <Label htmlFor="timezone">Timezone</Label>
                <Input
                  id="timezone"
                  defaultValue="Australia/Sydney"
                  className="w-[300px]"
                  readOnly
                />
              </div>
              <Button className="mt-2">Save Settings</Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Booking Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Booking</DialogTitle>
            <DialogDescription>
              Schedule a new meeting or appointment
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="booking-title">Title</Label>
              <Input
                id="booking-title"
                value={newBooking.title}
                onChange={(e) =>
                  setNewBooking({ ...newBooking, title: e.target.value })
                }
                placeholder="Meeting title"
              />
            </div>
            <div>
              <Label htmlFor="booking-client">Client</Label>
              <Input
                id="booking-client"
                value={newBooking.clientName}
                onChange={(e) =>
                  setNewBooking({ ...newBooking, clientName: e.target.value })
                }
                placeholder="Client name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="booking-date">Date</Label>
                <Input
                  id="booking-date"
                  type="date"
                  value={newBooking.date}
                  onChange={(e) =>
                    setNewBooking({ ...newBooking, date: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="booking-time">Time</Label>
                <Input
                  id="booking-time"
                  type="time"
                  value={newBooking.time}
                  onChange={(e) =>
                    setNewBooking({ ...newBooking, time: e.target.value })
                  }
                />
              </div>
            </div>
            <div>
              <Label htmlFor="booking-duration">Duration (minutes)</Label>
              <Select
                value={newBooking.duration}
                onValueChange={(value) =>
                  setNewBooking({ ...newBooking, duration: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="45">45 minutes</SelectItem>
                  <SelectItem value="60">60 minutes</SelectItem>
                  <SelectItem value="90">90 minutes</SelectItem>
                  <SelectItem value="120">120 minutes</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateBooking}
              disabled={!newBooking.title.trim() || !newBooking.date || !newBooking.time}
            >
              Create Booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}

// ── Stat Card ─────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold">{value}</p>
          </div>
          {icon}
        </div>
      </CardContent>
    </Card>
  );
}
